import { BalanceJson, PartKey, Reward, UserId, Uuid } from '../../models/Base';
import { RepositoryElement } from '../../models/Repository';
import { deepTransformDate, deepTransformTimestamp } from '../../utils/date';

export type ChallengeRecordType =
  | 'comprehensiveChallenge'
  | 'superChallenge'
  | `pathChallenge_${PartKey}`;
export enum ChallengeRecordMode {
  Simple = 'simple',
  Millionaire = 'millionaire',
}

export interface OpponentRecord {
  playedby: string;
  durationInMs: number;
  score: number;
}

interface ChallengeGame {
  gameCollectionId: Uuid;
  gameId: Uuid;
  gameHash: string;
  reward: Reward;
  opponent: OpponentRecord | null;
  fee?: BalanceJson;
  durationInMs?: number;
  score?: number;
  bonusReward?: Reward;
  accumulatedReward?: Reward;
  hasLeft?: boolean;
}

export interface ChallengeRecordJson<T = number> {
  challengeRecordId: Uuid;
  classroomId: Uuid;
  gameSetId: Uuid;
  type: ChallengeRecordType;
  playedBy: UserId;
  createdAt: T;
  startedAt: T;
  endedAt?: T;
  mode: ChallengeRecordMode;
  isFirstTime: boolean;
  games: ChallengeGame[];
}

export class ChallengeRecord
  extends RepositoryElement
  implements ChallengeRecordJson<Date>
{
  challengeRecordId!: Uuid;
  classroomId!: Uuid;
  gameSetId!: Uuid;
  type!: ChallengeRecordType;
  playedBy!: UserId;
  createdAt!: Date;
  startedAt!: Date;
  endedAt?: Date;
  mode!: ChallengeRecordMode;
  isFirstTime!: boolean;
  games!: ChallengeGame[];

  constructor(challengeRecord: ChallengeRecordJson<Date | number>) {
    super();
    Object.assign(this, deepTransformTimestamp(challengeRecord));
  }

  id(): string {
    return this.challengeRecordId;
  }

  toJSON(): ChallengeRecordJson {
    return deepTransformDate(this) as unknown as ChallengeRecordJson;
  }

  get completed(): boolean {
    return this.endedAt !== undefined;
  }

  get canPassStation(): boolean {
    return this.completed;
  }

  get score(): number | undefined {
    if (this.completed) {
      const total = this.games.length;
      return total > 0
        ? this.games.reduce((acc, cur) => acc + (cur.score || 0), 0) / total
        : 0;
    }
    return undefined;
  }

  get durationInMs(): number | undefined {
    if (this.completed) {
      return this.games.reduce((acc, cur) => acc + (cur.durationInMs || 0), 0);
    }
    return undefined;
  }

  get reward(): Reward | undefined {
    return this.games[this.games.length - 1]?.reward;
  }
}
